import { Api } from '@/Api';
import { defineStore } from 'pinia';
import { useDebug, DEBUG_NAMESPACE } from '../../../composables/useDebug';

const { debug } = useDebug(DEBUG_NAMESPACE.STORE);

export interface Reaction {
  id?: string;
  name: string;
  count: number;
  highlight?: boolean;
}

export interface ReactionTimelineEvent {
  name: string;
  videoTime: number;
}

export interface ReactionState {
  list: Reaction[];
  reactionEvents: ReactionTimelineEvent[];
  loadRequestId: number;
}

const eventKey = (event: ReactionTimelineEvent) => `${event.name}:${event.videoTime}`;

export const useReactionsStore = defineStore('reactionsStore', {
  state: () =>
    ({
      list: [
        { name: 'thumbsup', count: 0, highlight: false },
        { name: 'thumbsdown', count: 0, highlight: false },
        { name: 'fire', count: 0, highlight: false },
        { name: 'heart', count: 0, highlight: false },
      ],
      reactionEvents: [],
      loadRequestId: 0,
    }) as ReactionState,
  getters: {
    emojiList: state => state.list.map(l => l.name),
    reactions: state => state.list,
    isActiveReaction() {
      return (name: string) => {
        const reaction = this.getByName(name);

        return reaction ? reaction.highlight : false;
      };
    },
    getByName: state => (name: string) => state.list.find(r => r.name === name),
    reactionsCount(state) {
      return (reaction: string) => {
        const reactionFound = state.list.find(r => r.name === reaction);

        return reactionFound ? reactionFound.count : 0;
      };
    },
  },
  actions: {
    async load(itemId: string) {
      const requestId = ++this.loadRequestId;
      this.reactionEvents = [];
      this.list.forEach(reaction => {
        reaction.count = 0;
        reaction.id = undefined;
        reaction.highlight = false;
      });

      try {
        const data = await Api.item.reactions.list(itemId);
        if (requestId !== this.loadRequestId) {
          return;
        }

        data.forEach(res => {
          const reaction = this.getByName(res.name);
          if (!reaction) {
            return;
          }

          reaction.id = res.id;
          reaction.count = res.count;
        });

        // Keyed on name+time, not an id, so two viewers reacting with the same
        // emoji at the exact same timestamp collapse to one marker on reload.
        const persistedEvents = data.flatMap(res => res.videoTimes.map(videoTime => ({ name: res.name, videoTime })));
        const persistedKeys = new Set(persistedEvents.map(eventKey));
        this.reactionEvents = [...persistedEvents, ...this.reactionEvents.filter(event => !persistedKeys.has(eventKey(event)))];
      } catch (error) {
        debug(error);
      }
    },
    async addReaction(name: string, id: string, videoTime?: number): Promise<boolean> {
      const reaction = this.getByName(name);
      if (!reaction) {
        return false;
      }

      try {
        const res = await Api.item.reactions.add(name, id, videoTime);

        reaction.count = res.count;
        reaction.id = res.id;
        reaction.highlight = true;

        // The response carries the markers the server retained, so this emoji's
        // events are replaced rather than appended.
        this.reactionEvents = [...this.reactionEvents.filter(event => event.name !== name), ...res.videoTimes.map(time => ({ name, videoTime: time }))];
        return true;
      } catch (error) {
        debug(error);
        return false;
      }
    },
    async removeReaction(name: string) {
      const reaction = this.getByName(name);
      if (!reaction?.id || reaction.count === 0) {
        return;
      }

      try {
        const res = await Api.item.reactions.remove(reaction.id);
        reaction.count = res.count;
        reaction.highlight = false;
        this.reactionEvents = [...this.reactionEvents.filter(event => event.name !== name), ...res.videoTimes.map(time => ({ name, videoTime: time }))];
      } catch (error) {
        debug(error);
      }
    },
  },
});
